/**
 * Vertex Groups panel — Blender-style named-set management in edit mode.
 *
 * Users create groups, rename them inline, assign / remove the current
 * selection to/from the active group, and select a group back into the
 * viewport. The panel is edit-mode-only and hides itself for curves —
 * vertex groups are a mesh-only primitive.
 *
 * All behaviour lives in the store (see storeEditOps.ts). This component is
 * a thin, dispatch-only shell that mirrors ModifiersPanel's list idiom so
 * the two panels feel like siblings.
 */

import { Group as GroupIcon } from "lucide-react";
import { useStore } from "../../app/store.ts";
import type { VertexGroup } from "../../modeling/types.ts";
import { Button } from "../Button.tsx";
import { CollapsiblePanel, ListControl, Toggle } from "../shared.tsx";
import {
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SUBTLE,
  FONT_SIZE_SMALL,
} from "../tokens.ts";

// ---------------------------------------------------------------------------
// Panel shell
// ---------------------------------------------------------------------------

export function VertexGroupsPanel({ bare }: { bare?: boolean } = {}) {
  const editMode = useStore((s) => s.editMode);
  const obj = useStore((s) =>
    editMode ? s.sceneObjects.get(editMode.objectId) : undefined,
  );

  // Gate: mesh-only, edit-mode-only.
  const unavailable = !editMode || !obj || editMode.isCurve;

  const groups: VertexGroup[] = unavailable ? [] : (obj.vertexGroups ?? []);
  const groupCount = groups.length;

  const content = unavailable ? (
    <div
      style={{
        fontSize: FONT_SIZE_SMALL,
        color: COLOR_TEXT_FAINT,
        padding: "4px 0",
      }}
    >
      Enter edit mode on a mesh to manage vertex groups.
    </div>
  ) : (
    <VertexGroupsBody
      objectId={editMode.objectId}
      groups={groups}
      activeGroupId={editMode.activeVertexGroupId ?? null}
      selectionSize={editMode.selectedVertices.size}
      showColors={editMode.showVertexGroupColors ?? true}
    />
  );

  if (bare) return content;

  return (
    <CollapsiblePanel
      id="vertexGroups"
      title="Vertex Groups"
      icon={<GroupIcon size={11} />}
      summary={groupCount > 0 ? `${groupCount}` : undefined}
      defaultCollapsed={groupCount === 0}
    >
      {content}
    </CollapsiblePanel>
  );
}

// ---------------------------------------------------------------------------
// Body — requires a resolved edit session (object + editMode both live)
// ---------------------------------------------------------------------------

function VertexGroupsBody({
  objectId,
  groups,
  activeGroupId,
  selectionSize,
  showColors,
}: {
  objectId: string;
  groups: VertexGroup[];
  activeGroupId: string | null;
  selectionSize: number;
  showColors: boolean;
}) {
  const createVertexGroup = useStore((s) => s.createVertexGroup);
  const deleteVertexGroup = useStore((s) => s.deleteVertexGroup);
  const renameVertexGroup = useStore((s) => s.renameVertexGroup);
  const setActiveVertexGroup = useStore((s) => s.setActiveVertexGroup);
  const assignSelectionToGroup = useStore((s) => s.assignSelectionToGroup);
  const removeSelectionFromGroup = useStore((s) => s.removeSelectionFromGroup);
  const selectVertexGroup = useStore((s) => s.selectVertexGroup);
  const setShowVertexGroupColors = useStore((s) => s.setShowVertexGroupColors);

  const activeGroup = groups.find((g) => g.id === activeGroupId) ?? null;
  const canAssign = activeGroup !== null && selectionSize > 0;
  const canRemove = canAssign;
  const canSelect = activeGroup !== null && activeGroup.indices.size > 0;
  const canDeselect = canSelect;

  const items = groups.map((g) => ({
    id: g.id,
    label: g.name,
    trailing: (
      <span
        style={{
          fontSize: FONT_SIZE_SMALL,
          color: COLOR_TEXT_SUBTLE,
          flexShrink: 0,
        }}
      >
        {g.indices.size}
      </span>
    ),
  }));

  return (
    <>
      <ListControl
        items={items}
        activeId={activeGroupId}
        onSelect={setActiveVertexGroup}
        onAdd={() => createVertexGroup(objectId)}
        onDelete={(id) => deleteVertexGroup(objectId, id)}
        onRename={(id, name) => renameVertexGroup(objectId, id, name)}
        emptyMessage="No vertex groups"
        maxHeight={160}
      />

      {/* Action row — operates on the active group */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 4,
          marginTop: 8,
        }}
      >
        <ActionButton
          label="Assign"
          title="Add the current selection to the active group"
          enabled={canAssign}
          onClick={() =>
            activeGroup && assignSelectionToGroup(objectId, activeGroup.id)
          }
        />
        <ActionButton
          label="Remove"
          title="Remove the current selection from the active group"
          enabled={canRemove}
          onClick={() =>
            activeGroup && removeSelectionFromGroup(objectId, activeGroup.id)
          }
        />
        <ActionButton
          label="Select"
          title="Select the active group's vertices in the viewport"
          enabled={canSelect}
          onClick={() =>
            activeGroup && selectVertexGroup(objectId, activeGroup.id, "add")
          }
        />
        <ActionButton
          label="Deselect"
          title="Remove the active group's vertices from the selection"
          enabled={canDeselect}
          onClick={() =>
            activeGroup &&
            selectVertexGroup(objectId, activeGroup.id, "subtract")
          }
        />
      </div>

      <Toggle
        label="Show Surface Colors"
        checked={showColors}
        onChange={setShowVertexGroupColors}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Action button — small, uniform, disable-aware
// ---------------------------------------------------------------------------

function ActionButton({
  label,
  title,
  enabled,
  onClick,
}: {
  label: string;
  title: string;
  enabled: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      onClick={enabled ? onClick : undefined}
      disabled={!enabled}
      title={title}
      variant="secondary"
      size="xs"
      fullWidth
    >
      {label}
    </Button>
  );
}
